/// <reference path="global.d.ts" />
import React, { useEffect, useState, useRef, useSyncExternalStore } from 'react';
import { Flex, Text, Button, Container, Avatar, Card, IconButton, Box, Badge, Skeleton } from '@radix-ui/themes';
import { ArrowDownIcon, PlayIcon, UpdateIcon, HomeIcon, QuestionMarkCircledIcon, PaperPlaneIcon } from '@radix-ui/react-icons';
import { AnimatePresence, HTMLMotionProps, motion } from "framer-motion";
import TypingBubbles from './components/TypingBubbles';
import Conversation, { getConversation, AttachmentItem, PromptMessage, Link } from './Conversation';
import './App.css';
import type { Language } from './languages';
import { getLanguage, getLanguageName } from './languages';
import { fetchLogs } from './api';

// import confetti from 'canvas-confetti';

import { RecordIcon } from './icons/RecordIcon';
// import { EyeClosedIcon } from './icons/EyeClosedIcon';
import { speak, writeError } from './api';
import { ReceivedMessage, MessageOptions, SentMessage } from './components/Message';
import { SettingsProps, Settings } from './components/Settings';
import { debounce } from './debounce';
import { Audio, AudioState, audio as audioObserver } from './audio/audio';
import { ProgressBar } from './components/ProgressBar';
import type { Event } from './components/LogViewer';
import { LogViewer } from './components/LogViewer';
import { useLocalize, localize } from './localize';
import { getPushDeviceId, isPushEnabled } from './push';
import { SpectrumIcon } from './icons/SpectrumIcon';
import { clearPushNotifications } from './push';

let disableScroll = false;
const scrollToBottom = () =>
  window.scroll({
    behavior: 'smooth',
    top: document.body.scrollHeight + 100,
  });

type LocationData = { city: string, region_code: string; };
type LocationLookup = { [ip: string]: LocationData };

const useConversationState = (conversation: Conversation) => useSyncExternalStore(
  (listener) => conversation.subscribe(listener),
  () => conversation.getState(),
  () => conversation.getState(),
);
const useAudioState = (audio: Audio) => useSyncExternalStore(
  (listener) => audio.subscribe(listener),
  () => audio.getState(),
  () => audio.getState(),
);

const formatReviewHours = (ms: number) => {
  const clamped = Math.max(ms, 0);
  const hours = clamped / (60 * 60 * 1000);
  if (hours >= 24) {
    const days = hours / 24;
    const roundedDays = Math.round(days * 10) / 10;
    return `${roundedDays}d`;
  }
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(clamped / (60 * 1000)));
    return `${minutes}m`;
  }
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded}h`;
};

const formatReviewCountdown = (ms: number) => {
  const clamped = Math.max(ms, 0);
  let totalSeconds = Math.ceil(clamped / 1000);
  const seconds = totalSeconds % 60;
  totalSeconds = Math.floor(totalSeconds / 60);
  const minutes = totalSeconds % 60;
  totalSeconds = Math.floor(totalSeconds / 60);
  const hours = totalSeconds % 24;
  const days = Math.floor(totalSeconds / 24);
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');
  const paddedHours = String(hours).padStart(2, '0');
  if (days > 0) {
    return `${days}d ${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }
  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
};

const formatReviewDue = (ms: number) => {
  if (ms >= 0) {
    return `in ${formatReviewHours(ms)}`;
  }
  return `${formatReviewHours(Math.abs(ms))} ago`;
};

const REVIEW_STAGE_LABELS = ['2m', '10m', '1h', '6h', '1d', '3d', '1w', '4w', '12w'];
const REVIEW_SOON_WINDOW_MS = 6 * 60 * 60 * 1000;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const getStageProgress = (stageIndex: number) => clamp((stageIndex + 1) / REVIEW_STAGE_LABELS.length, 0.1, 1);

const App = React.memo(() => {
  const [logs, setLogs] = useState<{ events: Event[], locations: LocationLookup } | null>(null);

  useEffect(() => {
    if (window.location.search.includes('logs')) {
      fetchLogs().then(setLogs);
    }
  }, [])

  useEffect(() => {
    if (import.meta.env.DEV && import.meta.hot) {
      const reload = () => window.location.reload();
      import.meta.hot.accept('./Conversation', reload);
      import.meta.hot.accept('./audio/audio', reload);
    }
  }, []);

  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('DirtyLingoLanguage');
    if (stored) {
      return getLanguage(stored);
    }
    return getLanguage(
      decodeURIComponent(window.location.hash).substring(1)
    );
  });

  const [currentCode, setCurrentCode] = useLocalize('en');

  const conversation = getConversation(language, currentCode);
  useConversationState(conversation);
  const audioState = useAudioState(audioObserver);

  const [started, setStarted] = useState(false);
  const [recordMode, setRecordMode] = useState<'auto' | 'button'>(() => {
    const stored = localStorage.getItem('DirtyLingoRecordMode');
    return stored === 'auto' ? 'auto' : 'button';
  });
  useEffect(() => {
    conversation.initSync();
  }, [conversation]);

  useEffect(() => {
    if (recordMode === 'button') {
      void audioObserver.recordStop();
      void audioObserver.startPrebuffer();
    } else {
      audioObserver.stopPrebuffer();
    }
    return () => {
      audioObserver.stopPrebuffer();
    };
  }, [recordMode]);

  useEffect(() => {
    if (isPushEnabled()) {
      conversation.setPushEnabledDevice(true, getPushDeviceId());
    }
    conversation.startActivity();
    const onHidden = () => {
      if (document.visibilityState === 'hidden') {
        conversation.finishActivity();
      }
    };
    const onUnload = () => {
      conversation.finishActivity();
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [conversation]);

  useEffect(() => {
    void clearPushNotifications();
  }, []);

  if (conversation == null) {
    return;
  }

  const onStart = async () => {
    try {
      setStarted(true);
      await conversation.start();

    } catch { }
  }

  const onLanguageChange = (language: Language) => {
    setStarted(false);
    window.location.hash = language.name
    localStorage.setItem('DirtyLingoLanguage', language.name);
    setLanguage(language);
  };

  const reviewCount = conversation.reviewableExpressions.length;
  const nextReviewAt = conversation.nextReviewAt;

  const settingsProps: SettingsProps = {
    defaultOpen: false,
    forceOpen: audioState === 'init',
    doneText: localize(conversation.started === false ? 'Start' : 'Continue'),
    language,
    onLanguageChange,
    source: getLanguage(getLanguageName(currentCode)),
    onSourceChange: (source: Language) => { setCurrentCode(source.code); },
    nextReviewAt,
    reviewCount,
    recordMode,
    onRecordModeChange: (mode) => {
      setRecordMode(mode);
      localStorage.setItem('DirtyLingoRecordMode', mode);
    },
    onClearHistory: async () => {
      await conversation.clearHistory();
    },
    onCopyState: () => JSON.stringify(conversation.getSnapshot()),
    onPasteState: async (raw: string) => {
      const parsed = JSON.parse(raw);
      conversation.loadSnapshot(parsed);
    },
    onSyncChange: () => conversation.onSyncSessionChange(),
    onSyncNow: async () => {
      await conversation.syncNowManual();
    },
    onPushToggle: (enabled: boolean) => {
      conversation.setPushEnabledDevice(enabled, getPushDeviceId());
    },
    lastSyncAt: conversation.lastSyncTime,
    lastSyncError: conversation.lastSyncErrorMessage,
    onDone: async () => {
      if (!started) {
        await onStart();
      }
      if (audioObserver.is('muted')) {
        await audioObserver.record(() => {});
        await audioObserver.recordStop();
        return;
      }
      await audioObserver.record(
        async (file) => {
          if (audioObserver.is('loading')) {
            audioObserver.set('idle');
          }
          await conversation.continue(file);
        }
      );
    },
  }
  const chatProps: ChatProps = {
    conversation,
    audio: audioObserver,
  };

  return (
    <ChatContainer {...chatProps} settingsProps={settingsProps} recordMode={recordMode} >
      <>
        {logs && <LogViewer logs={logs} />}
        <Chat {...chatProps} />
      </>
    </ChatContainer>
  )
});

type ChatContainerProps = {
  settingsProps: SettingsProps;
  conversation: Conversation;
  audio: Audio;
  recordMode: 'auto' | 'button';
  children: JSX.Element | JSX.Element[];
};

const ChatContainer = React.memo(({ children, settingsProps, conversation, audio, recordMode }: ChatContainerProps) => {
  const sizingRef = useRef<HTMLSpanElement>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<number | null>(null);
  const [selectedBucket, setSelectedBucket] = useState<'ready' | 'soon' | 'later' | null>(null);
  const [now, setNow] = useState(Date.now());
  const conversationState = useConversationState(conversation);
  useEffect(() => {
    if (sizingRef.current) {
      const size = +getComputedStyle(sizingRef.current).fontSize.replace('px', '');
      if (size > 20) {
        document.body.classList.add('scale-container')
      }
    }
  }, [sizingRef]);
  useEffect(() => {
    if (!reviewOpen) {
      return;
    }
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [reviewOpen]);

  const shock = conversationState.shock;
  const lessonActive = conversation.lessonActive;
  const isFinale = lessonActive && conversation.isFinale;
  const lessonProgress = conversation.lessonProgress;
  const reviewSummary = conversation.reviewSummary;
  const nextReviewMs = reviewSummary.nextDueAt ? reviewSummary.nextDueAt - now : null;
  const stageBadges = reviewSummary.stageCounts
    .map((count, index) => ({ count, index }));
  const stageFilteredSchedule = selectedStage == null
    ? reviewSummary.schedule
    : reviewSummary.schedule.filter((entry) => entry.stage === selectedStage);
  const bucketFilteredSchedule = selectedBucket == null
    ? stageFilteredSchedule
    : stageFilteredSchedule.filter((entry) => {
      if (entry.ready) {
        return selectedBucket === 'ready';
      }
      return entry.dueAt <= now + REVIEW_SOON_WINDOW_MS
        ? selectedBucket === 'soon'
        : selectedBucket === 'later';
    });
  const reviewSchedule = bucketFilteredSchedule.slice(0, 3);
  const maxStageCount = Math.max(1, ...stageBadges.map((stage) => stage.count));
  const stageMidpointCount = maxStageCount / 2;
  const reviewBuckets = [
    { label: 'Ready now', count: reviewSummary.readyCount },
    { label: 'Soon', count: reviewSummary.soonCount },
    { label: 'Later', count: reviewSummary.laterCount },
  ];
  const maxBucketCount = Math.max(1, ...reviewBuckets.map((bucket) => bucket.count));
  const bucketMidpointCount = maxBucketCount / 2;

  return (
    <Flex style={{ flexGrow: 1 }} direction="column">
      <Container className="header" size="1" grow="0" position="sticky" top="0">
        <Flex align="center" direction="column" pb="2">
          <Avatar mt="4" className={'avatar ' + (shock ? 'shock' : '')} src={"/tutor.png"} fallback="T" radius="full" size="4" />
          <Text size="1" my="1">Tutor</Text>
          
            <Box className='level' my="1" style={{width: '75%'}}>
              <Card>
                <Flex align="center" direction="column" gap="3" mb={lessonActive && lessonProgress.total > 0 ? '3' : '0'}>
                  <Flex align="center" gap="2">
                    <Badge variant='solid' color={ isFinale ? 'red' : 'blue' } radius='full' size="2">
                      <Text ml="2" mr="1">{isFinale ? 'ULTIMATE TEST' : 'WORDS'}</Text>
                      {isFinale || <Badge variant='surface' style={{marginRight: '-0.5rem'}} size="1">{conversation.wordCount}</Badge>}
                    </Badge>
                    {reviewSummary.total > 0 && (
                      <IconButton
                        variant="ghost"
                        size="1"
                        onClick={() => setReviewOpen(!reviewOpen)}
                        style={{ transform: reviewOpen ? 'rotate(180deg)' : undefined }}
                        aria-label="Toggle review drawer"
                      >
                        <ArrowDownIcon />
                      </IconButton>
                    )}
                  </Flex>
                </Flex>
                {lessonActive && lessonProgress.total > 0 && (
                  <ProgressBar red={isFinale ?? false} size={lessonProgress.total} current={lessonProgress.current} />
                )}
                {reviewOpen && reviewSummary.total > 0 && (
                  <Flex direction="column" gap="2" mt="2" className="review-drawer">
                    {nextReviewMs != null && nextReviewMs > 0 && (
                      <div className="review-next">
                        <span className="review-next-label">Next in</span>
                        <span className="review-next-time">{formatReviewCountdown(nextReviewMs)}</span>
                      </div>
                    )}
                    {reviewBuckets.length > 0 && (
                      <div className="review-bucket-chart" role="group" aria-label="Review readiness">
                        {reviewBuckets.map((bucket) => {
                          const fillHeight = bucket.count === 0
                            ? 0
                            : Math.round((bucket.count / maxBucketCount) * 100);
                          const showCountAbove = bucket.count > 0 && bucket.count < bucketMidpointCount;
                          const bucketTone = bucket.label === 'Ready now'
                            ? 'ready'
                            : bucket.label === 'Soon'
                            ? 'soon'
                            : 'later';
                          const activeClass = bucketTone === 'ready' && bucket.count > 0
                            ? 'review-bucket-ready-active'
                            : '';
                          const isActive = selectedBucket === bucketTone;
                          return (
                            <button
                              type="button"
                              key={bucket.label}
                              className={`review-bucket-bar review-bucket-${bucketTone} ${activeClass} ${isActive ? 'review-bucket-bar-active' : ''}`}
                              aria-pressed={isActive}
                              onClick={() => {
                                setSelectedBucket((current) => (current === bucketTone ? null : bucketTone));
                                setSelectedStage(null);
                              }}
                            >
                              <span className="review-bucket-track" style={{ '--fill': `${fillHeight}%` } as React.CSSProperties}>
                                <span
                                  className={bucket.count > 0 ? 'review-bucket-fill review-bucket-fill-active' : 'review-bucket-fill'}
                                  style={{ height: `${fillHeight}%` }}
                                >
                                  {!showCountAbove && (
                                    <span className="review-bucket-count review-bucket-count-in">
                                      {bucket.count}
                                    </span>
                                  )}
                                </span>
                                {showCountAbove && (
                                  <span className="review-bucket-count review-bucket-count-over">
                                    {bucket.count}
                                  </span>
                                )}
                              </span>
                              <span className="review-bucket-label">{bucket.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {stageBadges.length > 0 && (
                      <Flex direction="column" gap="2">
                        <div className="review-stage-chart" role="group" aria-label="Review stages">
                          {stageBadges.map((stage) => {
                            const isActive = selectedStage === stage.index;
                            const fillHeight = Math.round((stage.count / maxStageCount) * 100);
                            const showCountAbove = stage.count > 0 && stage.count < stageMidpointCount;
                            return (
                              <button
                                type="button"
                                key={stage.index}
                                className={isActive ? 'review-stage-bar review-stage-bar-active' : 'review-stage-bar'}
                                aria-pressed={isActive}
                                onClick={() => {
                                  setSelectedStage((current) => (current === stage.index ? null : stage.index));
                                  setSelectedBucket(null);
                                }}
                              >
                                <span className="review-stage-bar-track" style={{ '--fill': `${fillHeight}%` } as React.CSSProperties}>
                                  <span
                                    className={stage.count > 0 ? 'review-stage-bar-fill review-stage-bar-fill-active' : 'review-stage-bar-fill'}
                                    style={{ height: `${fillHeight}%` }}
                                  >
                                    {!showCountAbove && (
                                      <span className="review-stage-bar-count review-stage-bar-count-in">
                                        {stage.count}
                                      </span>
                                    )}
                                  </span>
                                  {showCountAbove && (
                                    <span className="review-stage-bar-count review-stage-bar-count-over">
                                      {stage.count}
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="review-stage-legend" role="group" aria-label="Review stage filter">
                          {stageBadges.map((stage) => (
                            <div key={stage.index} className="review-stage-legend-item">
                              <button
                                type="button"
                                className="review-stage-legend-ring"
                                style={{ '--progress': getStageProgress(stage.index) } as React.CSSProperties}
                                aria-pressed={selectedStage === stage.index}
                                onClick={() => {
                                  setSelectedStage((current) => (current === stage.index ? null : stage.index));
                                  setSelectedBucket(null);
                                }}
                              >
                                <span>{REVIEW_STAGE_LABELS[stage.index] ?? `S${stage.index + 1}`}</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </Flex>
                    )}
                    {reviewSchedule.length > 0 && (
                      <Flex direction="column" gap="2" className="review-list" mt="2">
                        {reviewSchedule.map((entry) => {
                          const expression = conversation.getExpression(entry.index);
                          const label = expression.native ?? `#${entry.index + 1}`;
                          const dueMs = entry.dueAt - Date.now();
                          const dueText = formatReviewDue(dueMs);
                          return (
                            <Flex key={`${entry.index}-${entry.dueAt}`} align="center" justify="between" className="review-row">
                              <Flex align="center" gap="2" className="review-row-main">
                                <span
                                  className="review-ring"
                                  style={{ '--progress': getStageProgress(entry.stage) } as React.CSSProperties}
                                />
                                <Text size="1" className="review-row-label">{label}</Text>
                              </Flex>
                              <Text size="1" color={entry.ready ? 'ruby' : 'gray'} className="review-row-due">
                                {entry.ready ? 'due now' : dueText}
                              </Text>
                            </Flex>
                          );
                        })}
                      </Flex>
                    )}
                    {reviewSchedule.length === 0 && selectedStage != null && (
                      <Text size="1" color="gray">No expressions in this stage yet.</Text>
                    )}
                  </Flex>
                )}
              </Card>
            </Box>
          <Flex position="absolute" style={{ top: '2rem', right: '2rem' }}>
            <Settings {...settingsProps} />
          </Flex>
        </Flex>
      </Container>
      <Flex style={{ maxWidth: '100vw', overflow: 'hidden' }} direction="column" grow="1">
        {children}
      </Flex>
      <RecordFooter conversation={conversation} audio={audio} recordMode={recordMode} />
      <span ref={sizingRef} style={{ fontSize: '16px', font: '-apple-system-body' }}></span>
    </Flex>
  )
});

type ChatProps = {
  conversation: Conversation;
  audio: Audio;
}

const OPTIONS: ['Play' | 'Retry' | 'Ask', React.ReactNode][] = [
  ['Play', <PlayIcon />],
  ['Ask', <QuestionMarkCircledIcon />],
  ['Retry', <UpdateIcon />],
];

const MessageContent = ({ message }: { message: PromptMessage }) =>
  <>{message.content.split('\n').map((line, i) => (
    <Text key={i} size={message.role === 'assistant' && message.size || '3'}>{line}</Text>
  ))}
  </>;

type MessageWithOptionsProps = {
  message: PromptMessage;
  retry: boolean;
  conversation: Conversation;
  audio: Audio;
  messageProps: HTMLMotionProps<"div">;
}

const MessageWithOptions = React.memo(React.forwardRef<HTMLDivElement, MessageWithOptionsProps>((props, ref) => {
  const { message, messageProps, conversation, audio, retry } = props;
  const { playable } = message;
  const expressionIndex = (() => {
    if (message.role !== 'assistant') {
      return null;
    }
    const event = message.event;
    if ('index' in event && typeof event.index === 'number') {
      return event.index;
    }
    return null;
  })();
  const canAskBreakdown = message.role === 'assistant' && message.breakdownButton === true && typeof expressionIndex === 'number';

  const onMessageClick = async (name: string, message: PromptMessage) => {
    const selection = window.getSelection()?.toString();
    switch (name) {
      case 'Play':
        if (message.role === 'assistant') {
          const playRate = message.role === 'assistant' ? message.rate : 1;
          if (selection) {
            const selectionAudioUrl = await speak(selection);
            await audio.play(selectionAudioUrl, { rate: playRate });
          } else {
            const audioUrl = await speak(message.content);
            await audio.play(audioUrl, { rate: playRate });
          }
        } else {
          if (message.id) {
            const recording = await audio.getRecording(message.id);
            if (recording) {
            await audio.play(URL.createObjectURL(recording), {
              context: {
                message: message.content,
                role: message.role,
                eventId: message.event?.id,
              },
            });
            }
          }
        }
        break;
      case 'Retry':
        conversation.retry(message);
        break;
      case 'Ask':
        if (typeof expressionIndex === 'number') {
          await conversation.startBreakdown(expressionIndex);
        }
        break;
      default:
        break;
    }
  }

  const marginTop = message.role === 'assistant' && message.size === '5' && 'var(--space-4)' || undefined

  const options = OPTIONS.filter(([name]) => (
    (playable && name === 'Play')
    || (canAskBreakdown && name === 'Ask')
    || (retry && name === 'Retry')
  ));

  return (
    message.role === 'assistant' ? (
      <motion.div ref={ref} {...messageProps} style={{ originX: 0, marginTop }}>
        <ReceivedMessage>
          <Flex direction="column" position="relative">
            <MessageContent message={message} />
            <MessageOptions
                variant="soft"
                options={options}
                onClick={(name) => onMessageClick(name, message)} />
            {message.links && (
              <MessageLinks links={message.links} conversation={conversation} audio={audio} />
            )}
          </Flex>
        </ReceivedMessage>
      </motion.div>
    ) : (
      <motion.div ref={ref} {...messageProps} style={{ originX: 1 }}>
        <SentMessage tapbacks={message.tapbacks}>
          <Flex direction="column" align="end" position="relative" style={{ overflow: 'scroll' }}>
            <MessageContent message={message} />
            <MessageOptions
              options={options}
              onClick={(name) => onMessageClick(name, message)} />
          </Flex>
        </SentMessage>
      </motion.div>
    )
  )
}));

type MessageLinksProps = {
  conversation: Conversation;
  audio: Audio;
  links: Link[];
}

const MessageLinks = React.memo(({ links: attachment, conversation, audio }: MessageLinksProps) => {
  const [attachmentIndex, setAttachmentIndex] = useState(-1);
  const [attachmentData, setAttachmentData] = useState<AttachmentItem[] | null>(null);
  
  useEffect(() => {
    disableScroll = true;
    setAttachmentData(null);
    if (attachment[attachmentIndex]) {
      conversation.getAttachments(attachment[attachmentIndex])
        .then(o => {
          setAttachmentData(o);
          setTimeout(() => {
            disableScroll = false
          }, 100);
        })
        .catch(e => writeError(e));
    }
  }, [attachmentIndex])

  const activeLink = attachment[attachmentIndex];
  const resolveOverviewValue = (itemName: string): string => {
    if (!activeLink || activeLink.type !== 'overview') {
      return '';
    }
    const expression = conversation.getExpression(activeLink.index);
    if (itemName === 'Expression') {
      return expression.value;
    }
    if (itemName === 'Pronunciation') {
      return expression.pronunciation ?? '';
    }
    if (itemName === 'Translation') {
      return expression.native ?? '';
    }
    if (itemName === 'Description') {
      return expression.breakdown ?? '';
    }
    return '';
  };

  const refreshDetail = async (itemName: string): Promise<void> => {
    if (!activeLink || activeLink.type !== 'overview') {
      return;
    }
    await conversation.refreshExpressionDetail(activeLink.index, itemName);
    setAttachmentData((current) => current?.map((item) => (
      item.name === itemName
        ? { ...item, value: resolveOverviewValue(itemName) }
        : item
    )) ?? null);
  };

  const playAttachmentAudio = async (index: number, rate = 1): Promise<void> => {
    const expression = conversation.getExpression(index);
    const audioUrl = await speak(expression.value);
    await audio.play(audioUrl, {
      rate,
      context: {
        message: expression.value,
        role: 'assistant',
        eventId: String(index),
      },
    });
  };

  const parseBreakdownLines = (text: string): { word: string; phoneme: string; definition: string }[] => {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [word = '', phoneme = '', definition = ''] = line.split('|').map((part) => part.trim());
        if (!word) {
          return null;
        }
        return { word, phoneme, definition };
      })
      .filter((line): line is { word: string; phoneme: string; definition: string } => Boolean(line));
  };

  const playBreakdownWord = async (word: string): Promise<void> => {
    const audioUrl = await speak(word);
    await audio.play(audioUrl, {
      context: {
        message: word,
        role: 'assistant',
        eventId: 'breakdown',
      },
    });
  };

  const isOverview = activeLink?.type === 'overview';
  const isRemoved = isOverview ? conversation.isExpressionRemoved(activeLink.index) : false;
  const onToggleRemove = (): void => {
    if (!isOverview) {
      return;
    }
    conversation.toggleExpressionRemoved(activeLink.index);
    if (!isRemoved) {
      conversation.skipExpression(activeLink.index);
    }
    setAttachmentData((current) => current ? [...current] : current);
  };

  return (
    <Box my="1">
      {attachment.map(({ name }, i) => (
        <Button key={i} mr="2" mt="2" color="blue" variant='soft' size="1"
          onClick={() => setAttachmentIndex((current) => (current === i ? -1 : i))}
          // style={{ color: conversation.started ? 'transparent' : undefined }}
        >{localize(name)}</Button>
      ))}
      {attachmentIndex > -1 && (
        <Skeleton loading={attachmentIndex > -1 && !attachmentData}>
          <Card mt="3" mb="1">
            <Flex direction="column" gap="3">
              {attachmentData?.length && attachmentData.map((c, i) => (
                <Flex key={i} direction="column" gap="1">
                  <Flex align="center" gap="2">
                    <Text weight="bold" size="3">{c.name ? localize(c.name) : <span>&nbsp;</span> }</Text>
                    {activeLink?.type === 'overview' && ['Audio', 'Pronunciation', 'Translation', 'Description'].includes(c.name) && (
                      <IconButton color="blue" variant="soft" size="1" aria-label={localize('Refresh')}
                        onClick={() => refreshDetail(c.name)}>
                        <UpdateIcon />
                      </IconButton>
                    )}
                  </Flex>
                  <Flex align="center" gap="2">
                    {c.name === 'Description' && c.value.includes('|') ? (
                      <Flex direction="column" gap="2" width="100%">
                        {parseBreakdownLines(c.value).map((line, index) => (
                          <Flex key={`${line.word}-${index}`} align="center" gap="2">
                            <IconButton
                              color="blue"
                              variant="soft"
                              size="1"
                              aria-label={localize('Play')}
                              onClick={() => playBreakdownWord(line.word)}
                            >
                              <PlayIcon />
                            </IconButton>
                            <Text>
                              {line.word}{line.phoneme ? ` (${line.phoneme})` : ''}{line.definition ? `: ${line.definition}` : ''}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    ) : (
                      c.value
                    )}
                    {c.audio && (
                      <Flex align="center" gap="1">
                        <Button color="blue" variant="soft" size="1"
                          onClick={() => playAttachmentAudio(c.audio!.index, c.audio?.rate ?? 1)}>
                          <PlayIcon />
                          1x
                        </Button>
                        <Button color="blue" variant="soft" size="1"
                          onClick={() => playAttachmentAudio(c.audio!.index, 0.6)}>
                          <PlayIcon />
                          0.6x
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              ))}
              {isOverview && (
                <Flex>
                  <Button color={isRemoved ? 'gray' : 'red'} variant="soft" onClick={onToggleRemove}>
                    {localize(isRemoved ? 'Restore' : 'Remove')}
                  </Button>
                </Flex>
              )}
            </Flex>
          </Card>
        </Skeleton>
      )}
    </Box>
  );
});

const Chat = React.memo(({ conversation, audio }: ChatProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const audioState = useAudioState(audio);
  useConversationState(conversation);
  const audioIs = (...states: AudioState[]) => states.includes(audioState);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView()
  }

  const messages = conversation.messages;

  useEffect(() => {
    scrollToBottom()
  }, [messages?.length, audioState]);

  // useEffect(() => {
  //   confetti({
  //     particleCount: 100,
  //     startVelocity: 30,
  //     spread: 360,
  //     origin: {
  //       x: Math.random(),
  //       // since they fall down, start a bit higher than random
  //       y: Math.random() - 0.2
  //     }
  //   });
  // }, []);

  const motionProps: HTMLMotionProps<"div"> = {
    layout: true,
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: {
      opacity: { duration: 0.2 },
      layout: {
        type: 'just',
        bounce: 0.4,
        duration: 0.2,
        // duration: Math.abs(i - ((conversation.messages.length - 1))) * 0.15 + 0.45,
      },
    }
  };

  const messageProps = {
    conversation,
    audio,
  }


  const retry = (message: PromptMessage, i: number): boolean => {
    return (message.role === 'user') || (messages[i + 1]?.role ?? 'user') === 'user';
  }

  return (
    <Container size="1" m="2" grow="1">
      <AnimatePresence initial={false} mode="popLayout">
        {messages?.map((message, i) => (
          <MessageWithOptions key={i} {...messageProps}
            message={message}
            retry={retry(message, i)}
            messageProps={motionProps}
          />
        ))}
        {
          audioIs('loading', 'on') &&
          <motion.div key="recording" {...motionProps}>
            <SentMessage recording={audioIs('on') ? 'on' : audioIs('loading') ? 'loading' : undefined} bulge={audioIs('on')}>
              {audioIs('loading') ? (
                <TypingBubbles bright />
              ) : (
                <Text size="5">
                  <SpectrumIcon marginBottom='-0.5rem' />
                </Text>
              )}
            </SentMessage>
          </motion.div>
        }
        {
          conversation.typing === 'typing' &&
          <motion.div key="typing" {...messageProps}>
            <Box mt="2">
              <ReceivedMessage bulge={true}>
                <TypingBubbles />
              </ReceivedMessage>
            </Box>
          </motion.div>
        }
      </AnimatePresence>
      {/*         
      <ReceivedMessage>
        <Text size="9">🏆🏆🏆</Text>
      </ReceivedMessage>
      <ReceivedMessage>
        <Text size="6">Congratulations!</Text>
      </ReceivedMessage>
      <ReceivedMessage>
        You've made it to the 5 minute mark.
      </ReceivedMessage> 
    */}
      <div ref={messagesEndRef} />
    </Container>
  );
});

type RecordFooterProps = {
  conversation: Conversation;
  audio: Audio;
  recordMode: 'auto' | 'button';
}

const RecordFooter = React.memo(({ conversation, audio, recordMode }: RecordFooterProps) => {
  const audioState = useAudioState(audio);
  useConversationState(conversation);
  const audioIs = (...states: AudioState[]) => states.includes(audioState);
  const audioError = audio.getError();
  const buttonColor = recordMode === 'button' ? 'red' : 'green';
  const recordingColors: Record<AudioState, 'red' | 'green' | 'gray' | 'gray'> = {
    idle: buttonColor,
    on: buttonColor,
    init: 'gray',
    loading: recordMode === 'button' ? 'red' : 'gray',
    mute: recordMode === 'button' ? 'red' : 'gray',
    muted: recordMode === 'button' ? 'red' : 'gray',
  };
  const footerButtonStyle = { width: '4rem', height: '4rem', border: '1px solid gray' };
  const footerIconStyle = { width: 24, height: 24 };

  const isAtBottom = () => window.innerHeight + Math.round(window.scrollY) >= document.body.offsetHeight;
  const [atBottom, setAtBottom] = useState(isAtBottom());

  useEffect(() => {
    const listener = debounce(() => {
      const current = isAtBottom();
      if (current !== atBottom) {
        setAtBottom(current);
      }
    }, 500);

    window.addEventListener('scroll', listener);
    const resizer = new ResizeObserver(listener);
    resizer.observe(document.body);
    return () => {
      window.removeEventListener('scroll', listener);
      resizer.unobserve(document.body);
    };
  }, [atBottom, setAtBottom]);

  useEffect(() => {
    const listener = debounce(() => {
      if (!isAtBottom() && !disableScroll) {
        scrollToBottom();
      }
    }, 50);

    const resizer = new ResizeObserver(listener);
    resizer.observe(document.body);
    return () => {
      resizer.unobserve(document.body);
    };
  }, []);

  const onScrollDownClick = () => { setAtBottom(true); scrollToBottom() };

  return (
    <Container size="1" p="2" pb="5" grow="0" position="sticky" bottom="0">
      <Flex justify="center" align="center" gap="2">
        {!audioIs('init') && (
          <Flex direction="column" align="center" gap="1" style={{ position: 'relative' }}>
            {audioError && (
              <Badge color="red" variant="solid" size="1" style={{ position: 'absolute', bottom: '100%', marginBottom: '0.5rem' }}>
                <Text>{audioError}</Text>
              </Badge>
            )}
            <Button
              name="record"
              color={recordingColors[audioState]}
              size="4"
              style={footerButtonStyle}
              radius="full"
              onClick={async () => {
                if (recordMode === 'button') {
                  if (audioIs('on')) {
                    const file = await audio.manualRecordStop();
                    if (file) {
                      await conversation.stopPlayback();
                      await conversation.continue(file);
                    }
                    if (audio.is('loading')) {
                      audio.set('idle');
                    }
                    void audio.startPrebuffer();
                  } else {
                    await conversation.stopPlayback();
                    await audio.manualRecordStart();
                  }
                  return;
                }
                if (!audioIs('muted')) {
                  audio.set('muted');
                  await audio.recordStop();
                } else {
                  await audio.record(async (file) => {
                    if (audio.is('loading')) {
                      audio.set('idle');
                    }
                    await conversation.continue(file);
                  });
                }
              }}
            >
              <Flex justify="center" align="center">
                {recordMode === 'button'
                  ? (audioIs('on')
                    ? <PaperPlaneIcon width={footerIconStyle.width} height={footerIconStyle.height} />
                    : <RecordIcon muted={false} width="1.5em" height="1.5em" />)
                  : (audioIs('loading', 'mute')
                    ? <TypingBubbles bright />
                    : <RecordIcon muted={audioIs('muted')} width="1.5em" height="1.5em" />)}
              </Flex>
            </Button>
            {recordMode !== 'button' && audioIs('loading', 'mute') ? (
              <TypingBubbles bright />
            ) : (
              <Text size="1" color="gray">
                {recordMode === 'button'
                  ? (audioIs('on') ? localize('Recording') : localize('Record'))
                  : (audioIs('on', 'idle') ? localize('Mic on') : localize('Mic off'))}
              </Text>
            )}
          </Flex>
        )}
        {conversation.previousEvent && conversation.previousEvent.role !== 'ask_next' && (
          <Flex direction="column" align="center" gap="1">
            <IconButton
              size="4"
              variant="surface"
              radius="full"
              aria-label={localize('Home')}
              style={footerButtonStyle}
              onClick={async () => {
                await conversation.goToEntry();
              }}
            >
              <HomeIcon width={footerIconStyle.width} height={footerIconStyle.height} />
            </IconButton>
            <Text size="1" color="gray">{localize('Home')}</Text>
          </Flex>
        )}
        {!atBottom && (
          <Flex className='bounce' position="absolute" right="0" pr="5">
            <IconButton size="4" variant='surface' radius="full" style={footerButtonStyle} onClick={onScrollDownClick}>
              <ArrowDownIcon width={footerIconStyle.width} height={footerIconStyle.height} />
            </IconButton>
          </Flex>
        )}
      </Flex>
    </Container>
  )
});

export default App;
